import { FormEvent, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { classNames, formatCLP } from '../lib/format';
import { useDocumentMeta } from '../lib/seo';
import type { CartLine, CheckoutCustomer, CheckoutShipping } from '../types';

const CHILE_REGIONS = [
  { code: 'AP', name: 'Arica y Parinacota' },
  { code: 'TA', name: 'Tarapaca' },
  { code: 'AN', name: 'Antofagasta' },
  { code: 'AT', name: 'Atacama' },
  { code: 'CO', name: 'Coquimbo' },
  { code: 'VS', name: 'Valparaiso' },
  { code: 'RM', name: 'Region Metropolitana' },
  { code: 'LI', name: 'O’Higgins' },
  { code: 'ML', name: 'Maule' },
  { code: 'NB', name: 'Nuble' },
  { code: 'BI', name: 'Biobio' },
  { code: 'AR', name: 'Araucania' },
  { code: 'LR', name: 'Los Rios' },
  { code: 'LL', name: 'Los Lagos' },
  { code: 'AI', name: 'Aysen' },
  { code: 'MA', name: 'Magallanes' }
];

const FREE_SHIPPING_THRESHOLD = 90000;
const STANDARD_SHIPPING = 5000;

export function CheckoutPage({
  cart,
  navigate,
  updateQuantity
}: {
  cart: CartLine[];
  navigate: (path: string) => void;
  updateQuantity: (index: number, quantity: number) => void;
}) {
  useDocumentMeta({ title: 'Finalizar compra · Run More Chile', noindex: true });
  const [customer, setCustomer] = useState<CheckoutCustomer>({ name: '', email: '', phone: '', rut: '' });
  const [shipping, setShipping] = useState<CheckoutShipping>({ region: 'RM', city: '', address: '', notes: '' });
  const [coupon, setCoupon] = useState('');
  const [showCoupon, setShowCoupon] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; discount: number; freeShipping: boolean; description?: string } | null>(null);
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponMessage, setCouponMessage] = useState<string | null>(null);
  const [giftcardCode, setGiftcardCode] = useState('');
  const [appliedGiftcard, setAppliedGiftcard] = useState<{ code: string; balance: number; recipientName?: string } | null>(null);
  const [giftcardBusy, setGiftcardBusy] = useState(false);
  const [giftcardMessage, setGiftcardMessage] = useState<string | null>(null);
  const [showGiftcard, setShowGiftcard] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showSummary, setShowSummary] = useState(false);

  const subtotal = useMemo(
    () => cart.reduce((sum, line) => sum + (line.variant?.priceOverride || line.product.price) * line.quantity, 0),
    [cart]
  );
  const baseShipping = subtotal >= FREE_SHIPPING_THRESHOLD || subtotal === 0 ? 0 : STANDARD_SHIPPING;
  const shippingCost = appliedCoupon?.freeShipping ? 0 : baseShipping;
  const discount = appliedCoupon?.discount || 0;
  const totalBeforeGiftcard = Math.max(0, subtotal + shippingCost - discount);
  const giftcardApplied = appliedGiftcard ? Math.min(appliedGiftcard.balance, totalBeforeGiftcard) : 0;
  const total = Math.max(0, totalBeforeGiftcard - giftcardApplied);

  async function applyCoupon() {
    const code = coupon.trim();
    if (!code) return;
    setCouponBusy(true);
    setCouponMessage(null);
    try {
      const res = await api.checkCoupon(code);
      if (!res.valid) {
        setCouponMessage(res.message || 'Cupón no válido');
        return;
      }
      if (res.minOrder && subtotal < res.minOrder) {
        setCouponMessage(`Requiere pedido mínimo de ${formatCLP(res.minOrder)}`);
        return;
      }
      const discountAmount = res.discountType === 'percent'
        ? Math.round((subtotal * (res.discountValue || 0)) / 100)
        : Math.min(subtotal, res.discountValue || 0);
      setAppliedCoupon({
        code: res.code,
        discount: discountAmount,
        freeShipping: res.type === 'free_shipping',
        description: res.description
      });
    } catch (err) {
      setCouponMessage(err instanceof Error ? err.message : 'No se pudo validar');
    } finally {
      setCouponBusy(false);
    }
  }

  function clearCoupon() {
    setAppliedCoupon(null);
    setCoupon('');
    setCouponMessage(null);
  }

  async function applyGiftcard() {
    const code = giftcardCode.trim();
    if (!code) return;
    setGiftcardBusy(true);
    setGiftcardMessage(null);
    try {
      const res = await api.checkGiftcard(code);
      if (!res.valid) {
        setGiftcardMessage(res.message || 'Giftcard no válida');
        return;
      }
      setAppliedGiftcard({ code: res.code!, balance: res.balance!, recipientName: res.recipientName });
    } catch (err) {
      setGiftcardMessage(err instanceof Error ? err.message : 'No se pudo validar');
    } finally {
      setGiftcardBusy(false);
    }
  }

  function clearGiftcard() {
    setAppliedGiftcard(null);
    setGiftcardCode('');
    setGiftcardMessage(null);
  }
  const hasPreview = cart.some((line) => line.product.id.startsWith('preview-'));

  if (cart.length === 0) {
    return (
      <main className="checkout-empty">
        <div>
          <p className="eyebrow">Carro vacio</p>
          <h1>No hay productos en tu compra</h1>
          <p>Agrega productos desde la tienda para iniciar tu compra.</p>
          <button className="primary-button" onClick={() => navigate('/')}>Ver catalogo</button>
        </div>
      </main>
    );
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    if (hasPreview) {
      setError('Estos son productos demo. Cuando la tienda cargue su catalogo real podras comprar.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await api.checkout({
        customer,
        shipping,
        items: cart.map((line) => ({ productId: line.product.id, variantId: line.variant?.id, quantity: line.quantity })),
        couponCode: appliedCoupon?.code,
        giftcardCode: appliedGiftcard?.code
      });
      window.location.href = result.checkoutUrl;
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'No se pudo iniciar el pago');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="checkout-shell">
      <header className="checkout-header">
        <button className="brand-mark" onClick={() => navigate('/')}>
          <img src="/brand/runmore-logo.png" alt="Run More Chile" />
        </button>
        <span className="checkout-secure">
          <span aria-hidden="true">🔒</span> Compra segura · MercadoPago
        </span>
      </header>

      <button
        type="button"
        className="checkout-summary-toggle"
        onClick={() => setShowSummary((current) => !current)}
        aria-expanded={showSummary}
      >
        <span>{showSummary ? 'Ocultar resumen' : 'Mostrar resumen'} · {cart.length} {cart.length === 1 ? 'producto' : 'productos'}</span>
        <strong>{formatCLP(total)}</strong>
      </button>

      <div className={classNames('checkout-grid', showSummary && 'show-summary')}>
        <form className="checkout-form-column" onSubmit={submit}>
          <section className="checkout-step">
            <header className="step-header">
              <span className="step-number">01</span>
              <div>
                <h2>Contacto</h2>
                <p>Para enviarte la confirmacion del pedido.</p>
              </div>
            </header>
            <div className="step-fields">
              <label className="field">
                <span>Email</span>
                <input
                  type="email"
                  autoComplete="email"
                  value={customer.email}
                  onChange={(event) => setCustomer({ ...customer, email: event.target.value })}
                  placeholder="tucorreo@ejemplo.cl"
                  required
                />
              </label>
              <label className="field">
                <span>WhatsApp / Telefono</span>
                <input
                  type="tel"
                  autoComplete="tel"
                  value={customer.phone}
                  onChange={(event) => setCustomer({ ...customer, phone: event.target.value })}
                  placeholder="+56 9 0000 0000"
                  required
                />
              </label>
            </div>
          </section>

          <section className="checkout-step">
            <header className="step-header">
              <span className="step-number">02</span>
              <div>
                <h2>Despacho</h2>
                <p>A donde enviamos tu pedido. Despachamos a todo Chile via Starken.</p>
              </div>
            </header>
            <div className="step-fields">
              <div className="field-row two">
                <label className="field">
                  <span>Nombre completo</span>
                  <input
                    autoComplete="name"
                    value={customer.name}
                    onChange={(event) => setCustomer({ ...customer, name: event.target.value })}
                    placeholder="Maria Lopez"
                    required
                  />
                </label>
                <label className="field">
                  <span>RUT <em>opcional</em></span>
                  <input
                    value={customer.rut}
                    onChange={(event) => setCustomer({ ...customer, rut: event.target.value })}
                    placeholder="12.345.678-9"
                  />
                </label>
              </div>
              <label className="field">
                <span>Direccion</span>
                <input
                  autoComplete="street-address"
                  value={shipping.address}
                  onChange={(event) => setShipping({ ...shipping, address: event.target.value })}
                  placeholder="Calle, numero, depto"
                  required
                />
              </label>
              <div className="field-row two">
                <label className="field">
                  <span>Region</span>
                  <select
                    value={shipping.region}
                    onChange={(event) => setShipping({ ...shipping, region: event.target.value })}
                  >
                    {CHILE_REGIONS.map((region) => (
                      <option key={region.code} value={region.code}>{region.name}</option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>Comuna / Ciudad</span>
                  <input
                    autoComplete="address-level2"
                    value={shipping.city}
                    onChange={(event) => setShipping({ ...shipping, city: event.target.value })}
                    placeholder="Providencia"
                    required
                  />
                </label>
              </div>
              <label className="field">
                <span>Notas <em>opcional</em></span>
                <textarea
                  rows={2}
                  value={shipping.notes}
                  onChange={(event) => setShipping({ ...shipping, notes: event.target.value })}
                  placeholder="Ej: dejar en conserjeria, llamar antes de llegar..."
                />
              </label>

              <div className="coupon-block">
                {appliedGiftcard ? (
                  <div className="coupon-applied">
                    <span>🎁 Giftcard <strong>{appliedGiftcard.code}</strong> aplicada · saldo {formatCLP(appliedGiftcard.balance)}</span>
                    <button type="button" className="link-button" onClick={clearGiftcard}>Quitar</button>
                  </div>
                ) : showGiftcard ? (
                  <>
                    <div className="coupon-row">
                      <input value={giftcardCode} onChange={(e) => setGiftcardCode(e.target.value.toUpperCase())} placeholder="RM-XXXX-XXXX-XXXX" style={{ textTransform: 'uppercase' }} />
                      <button type="button" className="ghost-button compact" onClick={applyGiftcard} disabled={giftcardBusy}>
                        {giftcardBusy ? 'Validando...' : 'Aplicar'}
                      </button>
                    </div>
                    {giftcardMessage && <p className="muted small" style={{ color: '#a02020' }}>{giftcardMessage}</p>}
                  </>
                ) : (
                  <button type="button" className="coupon-toggle" onClick={() => setShowGiftcard(true)}>+ ¿Tienes una giftcard?</button>
                )}
              </div>

              <div className="coupon-block">
                {appliedCoupon ? (
                  <div className="coupon-applied">
                    <span><strong>{appliedCoupon.code}</strong> aplicado{appliedCoupon.description ? ` · ${appliedCoupon.description}` : ''}</span>
                    <button type="button" className="link-button" onClick={clearCoupon}>Quitar</button>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      className="coupon-toggle"
                      onClick={() => setShowCoupon((current) => !current)}
                      aria-expanded={showCoupon}
                    >
                      {showCoupon ? '−' : '+'} {showCoupon ? 'Ocultar' : '¿Tienes un'} codigo de descuento{showCoupon ? '' : '?'}
                    </button>
                    {showCoupon && (
                      <>
                        <div className="coupon-row">
                          <input
                            value={coupon}
                            onChange={(event) => setCoupon(event.target.value.toUpperCase())}
                            placeholder="EJ: PRIMERA10"
                          />
                          <button type="button" className="ghost-button compact" onClick={applyCoupon} disabled={couponBusy}>
                            {couponBusy ? 'Validando...' : 'Aplicar'}
                          </button>
                        </div>
                        {couponMessage && <p className="muted small" style={{ color: '#a02020' }}>{couponMessage}</p>}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </section>

          <section className="checkout-step">
            <header className="step-header">
              <span className="step-number">03</span>
              <div>
                <h2>Pago</h2>
                <p>Te llevamos al sitio seguro de MercadoPago para completar el pago.</p>
              </div>
            </header>
            <div className="step-fields">
              <div className="payment-card">
                <div className="payment-card-mark">
                  <strong>MercadoPago</strong>
                  <span>Tarjeta · Debito · Transferencia · Efectivo</span>
                </div>
                <span aria-hidden="true" className="payment-card-check">✓</span>
              </div>
            </div>
          </section>

          {error && <p className="form-message tone-error">{error}</p>}

          <div className="checkout-cta">
            <button type="button" className="link-button" onClick={() => navigate('/')}>← Seguir comprando</button>
            <button className="primary-button checkout-submit" disabled={submitting}>
              {submitting ? 'Creando pago...' : `Pagar ${formatCLP(total)}`}
            </button>
          </div>
        </form>

        <aside className="checkout-summary">
          <div className="checkout-summary-inner">
            <h2 className="summary-title">Tu compra <small>({cart.length})</small></h2>
            <ul className="summary-items">
              {cart.map((line, index) => {
                const unit = line.variant?.priceOverride || line.product.price;
                return (
                  <li key={`${line.product.id}-${line.variant?.id || 'default'}`}>
                    <div className="summary-thumb">
                      {line.product.images[0]?.url ? (
                        <img src={line.product.images[0].url} alt={line.product.name} />
                      ) : (
                        <span>{line.product.name.slice(0, 2).toUpperCase()}</span>
                      )}
                      <i>{line.quantity}</i>
                    </div>
                    <div className="summary-meta">
                      <strong>{line.product.name}</strong>
                      {(line.variant?.color || line.variant?.size) && (
                        <span>{[line.variant?.color, line.variant?.size].filter(Boolean).join(' / ')}</span>
                      )}
                      <div className="summary-qty">
                        <button type="button" onClick={() => updateQuantity(index, line.quantity - 1)} aria-label="Restar">−</button>
                        <span>{line.quantity}</span>
                        <button type="button" onClick={() => updateQuantity(index, line.quantity + 1)} aria-label="Sumar">+</button>
                      </div>
                    </div>
                    <span className="summary-price">{formatCLP(unit * line.quantity)}</span>
                  </li>
                );
              })}
            </ul>

            <dl className="summary-totals">
              <div>
                <dt>Subtotal</dt>
                <dd>{formatCLP(subtotal)}</dd>
              </div>
              {discount > 0 && (
                <div>
                  <dt>Descuento ({appliedCoupon?.code})</dt>
                  <dd style={{ color: '#146e3c' }}>-{formatCLP(discount)}</dd>
                </div>
              )}
              <div>
                <dt>Despacho</dt>
                <dd>{shippingCost === 0 ? <span className="badge-free">Gratis</span> : formatCLP(shippingCost)}</dd>
              </div>
              {giftcardApplied > 0 && (
                <div>
                  <dt>Giftcard ({appliedGiftcard?.code})</dt>
                  <dd style={{ color: '#146e3c' }}>-{formatCLP(giftcardApplied)}</dd>
                </div>
              )}
              <div className="summary-total-row">
                <dt>Total</dt>
                <dd>
                  <small>CLP</small>
                  {formatCLP(total)}
                </dd>
              </div>
            </dl>

            {subtotal > 0 && subtotal < FREE_SHIPPING_THRESHOLD && (
              <p className="free-shipping-hint">
                Te faltan <strong>{formatCLP(FREE_SHIPPING_THRESHOLD - subtotal)}</strong> para despacho gratis.
              </p>
            )}

            <ul className="summary-trust">
              <li>🔒 Pago 100% seguro</li>
              <li>📦 Despacho a todo Chile</li>
              <li>↩️ 30 dias para cambios</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
